import { useState, useCallback, useEffect } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';

type UrlStateOptions<T> = {
  name: string;
  defaultValue: T;
  serialize?: (value: T) => string;
  deserialize?: (value: string) => T;
  navigateMode?: 'push' | 'replace';
  skipSync?: boolean;
};

/**
 * Hook for syncing state with URL parameters
 * 
 * @param options Configuration options for the URL state
 * @returns [state, setState] - Similar to useState, but synchronized with URL
 */
export function useUrlState<T>(options: UrlStateOptions<T>): [T, (value: T | ((prev: T) => T)) => void] {
  const {
    name,
    defaultValue,
    serialize = defaultSerialize,
    deserialize = defaultDeserialize,
    navigateMode = 'push',
    skipSync = false
  } = options;

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Parse initial value from URL or use default
  const initialValue = searchParams.has(name) && !skipSync
    ? deserialize(searchParams.get(name) || '')
    : defaultValue;
    
  const [state, setState] = useState<T>(initialValue);

  // Effect for reading from URL when location changes
  useEffect(() => {
    if (skipSync) return;
    
    if (searchParams.has(name)) {
      const valueFromUrl = deserialize(searchParams.get(name) || '');
      setState(valueFromUrl);
    } else if (state !== defaultValue) {
      setState(defaultValue);
    }
  }, [location.search, name, skipSync]);

  // Update state and sync to URL in separate effect
  const updateState = useCallback((value: T | ((prev: T) => T)) => {
    setState(prev => {
      const nextValue = typeof value === 'function' 
        ? (value as ((prev: T) => T))(prev) 
        : value;
      
      return nextValue;
    });
  }, []);
  
  // Sync state to URL in a separate effect to avoid setState during render
  useEffect(() => {
    if (skipSync) return;
    
    // Create a new URLSearchParams with all existing parameters
    const newSearchParams = new URLSearchParams(searchParams.toString());
    
    // Update or remove the parameter
    if (state === defaultValue || state === undefined || state === null) {
      if (newSearchParams.has(name)) {
        newSearchParams.delete(name);
        // Only update URL if parameter actually existed
        const newSearch = newSearchParams.toString();
        const query = newSearch ? `?${newSearch}` : '';
        
        if (navigateMode === 'replace') {
          navigate({ pathname: location.pathname, search: query }, { replace: true });
        } else {
          setSearchParams(newSearchParams);
        }
      }
    } else {
      const serialized = serialize(state);
      if (serialized) {
        const currentValue = newSearchParams.get(name);
        if (currentValue !== serialized) {
          newSearchParams.set(name, serialized);
          // Only update URL if value actually changed
          const newSearch = newSearchParams.toString();
          const query = newSearch ? `?${newSearch}` : '';
          
          if (navigateMode === 'replace') {
            navigate({ pathname: location.pathname, search: query }, { replace: true });
          } else {
            setSearchParams(newSearchParams);
          }
        }
      } else {
        if (newSearchParams.has(name)) {
          newSearchParams.delete(name);
          const newSearch = newSearchParams.toString();
          const query = newSearch ? `?${newSearch}` : '';
          
          if (navigateMode === 'replace') {
            navigate({ pathname: location.pathname, search: query }, { replace: true });
          } else {
            setSearchParams(newSearchParams);
          }
        }
      }
    }
  }, [state, name, searchParams, navigate, location.pathname, setSearchParams, skipSync, defaultValue, serialize, navigateMode]);

  return [state, updateState];
}

/**
 * Default serializer for URL state values
 */
function defaultSerialize<T>(value: T): string {
  if (value === null || value === undefined) {
    return '';
  }
  
  if (typeof value === 'string') {
    return value;
  }
  
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  
  if (Array.isArray(value)) {
    return value.join(',');
  }
  
  return JSON.stringify(value);
}

/**
 * Default deserializer for URL state values
 */
function defaultDeserialize<T>(value: string): T {
  // Try to parse as JSON
  if (value.startsWith('{') || value.startsWith('[')) {
    try {
      return JSON.parse(value) as T;
    } catch (e) {
      // If JSON parsing fails, fall through to other cases
    }
  }
  
  // Parse boolean values
  if (value === 'true') return true as unknown as T;
  if (value === 'false') return false as unknown as T;
  
  // Parse numeric values
  if (/^-?\d+(\.\d+)?$/.test(value)) {
    const num = Number(value);
    if (!isNaN(num)) return num as unknown as T;
  }
  
  // Parse comma-separated arrays
  if (value.includes(',')) {
    const parts = value.split(',').map(part => part.trim());
    // Try to convert array items to proper types
    const typedParts = parts.map(part => {
      if (part === 'true') return true;
      if (part === 'false') return false;
      if (/^-?\d+(\.\d+)?$/.test(part)) {
        const num = Number(part);
        if (!isNaN(num)) return num;
      }
      return part;
    });
    return typedParts as unknown as T;
  }
  
  // Return as is for other cases
  return value as unknown as T;
}

/**
 * Type-specific utility hooks for common data types
 */

export function useUrlStringState(name: string, defaultValue: string = '', options?: Partial<Omit<UrlStateOptions<string>, 'name' | 'defaultValue'>>) {
  return useUrlState<string>({ name, defaultValue, ...options });
}

export function useUrlNumberState(name: string, defaultValue: number = 0, options?: Partial<Omit<UrlStateOptions<number>, 'name' | 'defaultValue'>>) {
  return useUrlState<number>({ name, defaultValue, ...options });
}

export function useUrlBooleanState(name: string, defaultValue: boolean = false, options?: Partial<Omit<UrlStateOptions<boolean>, 'name' | 'defaultValue'>>) {
  return useUrlState<boolean>({ name, defaultValue, ...options });
}

export function useUrlArrayState<T>(name: string, defaultValue: T[] = [], options?: Partial<Omit<UrlStateOptions<T[]>, 'name' | 'defaultValue'>>) {
  return useUrlState<T[]>({ name, defaultValue, ...options });
} 